import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, Fetcher, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { geoList, geoCreate, geoDelete, GEO_STR as S, type GeoConfigRow } from './geoApi'

/**
 * Umumiy A11 infra-reyestr CRUD jadvali. A11.13/14/15/16/17 ("ro'yxat +
 * qo'shish-form + o'chirish") ekranlari shu bitta komponent orqali quriladi
 * (seo/SeoConfigTable naqshi). Backend `/admin/geo/*` JONLI: ro'yxat fail-soft
 * bo'sh qaytishi mumkin, mutatsiya real POST/DELETE qiladi. Placeholder huquq
 * `COUNTRIES_VIEW` (boshqa geo ekranlar bilan bir xil).
 */
export interface GeoField {
  /** Yuboriladigan `body` kaliti. */
  name: string
  placeholder: string
  mono?: boolean
  /** Berilsa — <select> (bo'sh birinchi variant = placeholder). */
  options?: { value: string; label: string }[]
}

export interface GeoRegistryTableProps {
  screenId: string
  title: string
  description?: string
  addLabel: string
  /** Backend yo'li, masalan `/admin/geo/ip-ranges`. */
  path: string
  /** Ro'yxat javobidagi massiv kaliti, masalan `ranges` / `nodes` / `routes`. */
  listKey: string
  /** Tez qidiruvda solishtiriladigan maydonlar. */
  searchKeys: string[]
  fields: GeoField[]
  columns: AdminColumn<GeoConfigRow>[]
  icon: ReactNode
  /** PageHeader ostida, qo'shish-form ustidagi ixtiyoriy slot (masalan VPN tumbleri). */
  topSlot?: ReactNode
}

export function GeoRegistryTable({
  screenId,
  title,
  description,
  addLabel,
  path,
  listKey,
  searchKeys,
  fields,
  columns,
  icon,
  topSlot,
}: GeoRegistryTableProps) {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [form, setForm] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const firstName = fields[0]?.name ?? ''
  const canAdd = Boolean(form[firstName]?.trim())

  const add = async () => {
    if (!canAdd) return
    setBusy(true)
    try {
      const body: Record<string, string> = {}
      for (const f of fields) body[f.name] = (form[f.name] ?? '').trim()
      await geoCreate(path, body)
      toast(S.createdOk, { tone: 'success' })
      setForm({})
      setReloadKey((k) => k + 1)
    } catch {
      toast(S.opFailed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const rowActions = useMemo<RowAction<GeoConfigRow>[]>(
    () => [
      {
        id: 'delete',
        label: S.del,
        icon: <Trash2 size={14} />,
        danger: true,
        onSelect: async (r) => {
          try {
            await geoDelete(path, r.id)
            toast(S.deletedOk, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.opFailed, { tone: 'error' })
          }
        },
      },
    ],
    [path, toast],
  )

  // DataTable `fetcher` ni barqaror identity bilan kutadi (index.tsx effekti).
  const searchKeyStr = searchKeys.join('|')
  const fetcher = useMemo<Fetcher<GeoConfigRow>>(
    () => geoList<GeoConfigRow>(path, listKey, searchKeyStr.split('|')),
    [path, listKey, searchKeyStr],
  )

  return (
    <>
      <PageHeader
        title={title}
        screenId={screenId}
        description={description}
        actions={
          <Link
            to="/geo"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.backToList}
          </Link>
        }
      />
      {topSlot}
      <div className="flex flex-wrap items-center gap-2 p-4 pb-0">
        {fields.map((f) =>
          f.options ? (
            <select
              key={f.name}
              value={form[f.name] ?? ''}
              onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
              className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary outline-none focus:border-border-strong"
            >
              <option value="">{f.placeholder}</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              key={f.name}
              value={form[f.name] ?? ''}
              onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
              placeholder={f.placeholder}
              spellCheck={false}
              className={`min-w-[160px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong ${
                f.mono ? 'font-mono' : ''
              }`}
            />
          ),
        )}
        <button
          type="button"
          onClick={add}
          disabled={busy || !canAdd}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Plus size={14} />
          {addLabel}
        </button>
      </div>
      <DataTable<GeoConfigRow>
        key={reloadKey}
        screenId={screenId}
        permission={PERMISSIONS.COUNTRIES_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon }}
      />
    </>
  )
}
